import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Label } from "design-system/atoms/Label"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { isFilled } from "lopermut"
import { ChevronDown } from "lucide-react"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CapabilityCampaignAssignation, ConversationFlowEnum } from "schema"

type Props = {
  campaignAssignation: CapabilityCampaignAssignation
  setCampaignAssignation: (value: CapabilityCampaignAssignation) => void
  campaignIds: string[]
  setCampaignIds: (ids: string[]) => void
  campaignFlow: keyof typeof ConversationFlowEnum
  setCampaignFlow: (flow: keyof typeof ConversationFlowEnum) => void
}

export function CapabilityActivationSettings({
  campaignAssignation,
  setCampaignAssignation,
  campaignIds,
  setCampaignIds,
  campaignFlow,
  setCampaignFlow,
}: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })

  const { data: campaigns, isPending } = useCampaignsQuery({
    query: {
      query: debouncedSearchQuery,
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
      includeIds: isFilled(debouncedSearchQuery) ? undefined : campaignIds,
    },
  })

  const toggleCampaign = (campaignId: string) => {
    if (campaignIds.includes(campaignId)) {
      setCampaignIds(campaignIds.filter((id) => id !== campaignId))
    } else {
      setCampaignIds([...campaignIds, campaignId])
    }
  }

  const triggerLabel = useMemo(() => {
    if (
      campaignIds.length === 0 ||
      campaignAssignation !== CapabilityCampaignAssignation.HandPicked
    ) {
      return t("capabilities.activation.placeholder")
    }
    return t("capabilities.activation.selectLabel", { count: campaignIds.length })
  }, [campaignIds.length, campaignAssignation, t])

  const isHandPicked = campaignAssignation === CapabilityCampaignAssignation.HandPicked
  const isCampaignFlow = campaignAssignation === CapabilityCampaignAssignation.CampaignFlow

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label>{t("capabilities.activation.title")}</Label>
        <Select value={campaignAssignation} onValueChange={setCampaignAssignation}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CapabilityCampaignAssignation.AllCampaigns}>
              {t("capabilities.activation.allCampaignsOption")}
            </SelectItem>
            <SelectItem value={CapabilityCampaignAssignation.HandPicked}>
              {t("capabilities.activation.handPickedOption")}
            </SelectItem>
            <SelectItem value={CapabilityCampaignAssignation.CampaignFlow}>
              {t("capabilities.activation.campaignFlowOption")}
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {campaignAssignation === CapabilityCampaignAssignation.AllCampaigns &&
            t("capabilities.activation.allCampaigns")}
          {isHandPicked && t("capabilities.activation.handPicked")}
          {isCampaignFlow && t("capabilities.activation.campaignFlow")}
        </span>
      </div>

      {isHandPicked && (
        <div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                disabled={isPending}
              >
                <span className="truncate text-left">{triggerLabel}</span>
                <ChevronDown className="h-5 w-5 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 min-w-56" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={t("capabilities.activation.searchPlaceholder")}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  {(!campaigns || campaigns.length === 0) && (
                    <CommandEmpty>{t("capabilities.activation.emptyPlaceholder")}</CommandEmpty>
                  )}
                  <CommandGroup>
                    {campaigns?.map((campaign) => {
                      const isSelected = campaignIds.includes(campaign.id)
                      return (
                        <CommandItem
                          key={campaign.id}
                          value={campaign.id}
                          onSelect={() => toggleCampaign(campaign.id)}
                          className="gap-2"
                        >
                          <Checkbox checked={isSelected} />
                          <span className="truncate flex-1">
                            {campaign.name ?? t("campaigns.untitled")}
                          </span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      {isCampaignFlow && (
        <div className="flex flex-col gap-1.5">
          <Select
            value={campaignFlow}
            onValueChange={(val: keyof typeof ConversationFlowEnum) => setCampaignFlow(val)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Inbound">{t("capabilities.activation.flowInbound")}</SelectItem>
              <SelectItem value="Outbound">{t("capabilities.activation.flowOutbound")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
